import { useEffect, useMemo, useState } from "react"

import moment from "moment-timezone"
import { useNavigate } from "react-router-dom"

import { CoverageCheckResponseType } from "@common-shared/config/coverage.config"
import { PoliciesListType } from "@common-shared/config/oneVerify/oneVerifyPolicies.config"
import { OneVerifyPermission } from "@common-shared/config/rolesPermissions.config"
import { IPolicyIdentificationData } from "@common-shared/types/policies.types"
import {
    IOneVerifyCoverageCheckHistory,
    IOneVerifyCoverageOverride,
} from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { ConfirmationHelper } from "@front-shared/helpers/confirmation.helper"
import { ToastrHelper } from "@front-shared/helpers/toastr.helper"

import { GENERAL_CONFIG } from "../config/general.config"
import { CoverageHistoryHelper } from "../helpers/coverageHistory.helper"
import { PermissionsHelper } from "../helpers/permissions.helper"
import { PoliciesHelper } from "../helpers/policies.helper"
import { RuntimeHelper } from "../helpers/runtime.helper"
import { useAppDispatch } from "../store/appDispatch.hook"
import { useAppSelector } from "../store/selectors/app.selector"
import { coverageOverridesSelector } from "../store/selectors/coverageOverrides.selector"
import { runtimeSelector } from "../store/selectors/runtime.selector"
import { selectedPracticeRoleSelector } from "../store/selectors/selectedPracticeRole.selector"
import { coverageOverridesSetSelectedPolicyOverride } from "../store/slices/coverageOverrides.slice"
import {
    createCoverageOverrides,
    deleteSingleCoverageOverride,
} from "../store/thunks/coverageOverrides.thunks"
import type {
    ICoverageOverrideResult,
    IOverrideDefaultData,
} from "../types/data.types"
import { useBulkOverrides } from "./bulkOverrides.hooks"

export const useSingleOverride = (
    coverageCheckHistory: IOneVerifyCoverageCheckHistory | null,
    override: IOneVerifyCoverageOverride | null,
    nirvanaRequestId?: string,
    getSinglePolicy?: (
        withPolicyDataReFetch?: boolean,
        onSuccess?: () => void,
    ) => void,
    reloadPoliciesList?: () => void,
    navigateToPolicyByRequestIdProvided?: (
        nirvanaRequestId: string,
        withPoliciesListReload?: boolean,
    ) => void,
) => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    const {
        // vars
        isOverridesEnabled,
        suggestedOverrideExpirationDate,
        minOverrideExpirationDate,
        isCreateOverrideLoading,
        // state / form
        overridesFormKey,
        setOverridesFormKey,
        dataWatcher,
        overridesFormMethods,
        // methods
        getFormDefaultData,
        onUseSuggestedOverrideExpiration,
        onResetOverride,
        validateOverrideCreation,
        mapCreationFormData,
    } = useBulkOverrides()

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const runtimeState = useAppSelector(runtimeSelector)
    const overridesState = useAppSelector(coverageOverridesSelector)

    const [isOverrideModeActive, setIsOverrideModeActive] =
        useState<boolean>(false)

    const isGettingSinglePolicyLoading = useMemo(() => {
        return RuntimeHelper.isGetSinglePolicyHistoryLoading()
    }, [runtimeState.isLoading])

    const isDeleteOverrideLoading = useMemo(() => {
        return RuntimeHelper.isCoverageOverridesDeleteLoading()
    }, [runtimeState.isLoading])

    const isGetSingleOverrideLoading = useMemo(() => {
        return RuntimeHelper.isCoverageOverridesGetSingleLoading()
    }, [runtimeState.isLoading])

    const coverageCheckIdentificationData =
        useMemo<IPolicyIdentificationData | null>(() => {
            return CoverageHistoryHelper.getPolicyIdentificationData(
                coverageCheckHistory,
            )
        }, [coverageCheckHistory])

    const canOverridePolicyCoverage = useMemo<boolean>(() => {
        return (
            !!isOverridesEnabled &&
            PermissionsHelper.hasPermission(
                OneVerifyPermission.overrideCoverageData,
                selectedPracticeRole,
            ) &&
            !!coverageCheckHistory?.policy &&
            !!coverageCheckIdentificationData &&
            // Overrides are not enabled for agentic checks for now
            !coverageCheckHistory.agenticCheck &&
            // TODO enable overrides for Medicaid plans once we implement this feature
            coverageCheckHistory.coverageResult.responseType ===
                CoverageCheckResponseType.COVERAGE
        )
    }, [
        selectedPracticeRole?.practice.id,
        isOverridesEnabled,
        coverageCheckHistory?.policy,
        coverageCheckIdentificationData,
        coverageCheckHistory?.coverageResult.planType,
    ])

    const isOverrideActionsDisabled = useMemo<boolean>(() => {
        return (
            isGettingSinglePolicyLoading ||
            isDeleteOverrideLoading ||
            isGetSingleOverrideLoading
        )
    }, [
        isGettingSinglePolicyLoading,
        isDeleteOverrideLoading,
        isGetSingleOverrideLoading,
    ])

    const defaultData = useMemo<IOverrideDefaultData>(() => {
        return {
            // Combination of override and coverage result
            form: getFormDefaultData(
                override || null,
                coverageCheckHistory?.coverageResult || undefined,
            ),
            // Just from override
            override: getFormDefaultData(override || null, undefined, false),
        }
    }, [coverageCheckHistory?.coverageResult, override])

    // Set override default expiration data
    useEffect(() => {
        // Set default expiration date
        if (!override) {
            return
        }

        overridesFormMethods.setValue(
            "expirationDate",
            moment(override.expirationDate),
        )
    }, [override])

    const onNavigateToPolicyByRequestId = (
        nirvanaRequestId: string,
        withPoliciesListReload = true,
    ) => {
        if (navigateToPolicyByRequestIdProvided) {
            navigateToPolicyByRequestIdProvided(nirvanaRequestId)

            return
        }

        const neededUrl =
            PoliciesHelper.getSinglePolicyBaseUrl(
                PoliciesListType.OVERRIDES,
            ).replace(":nirvanaRequestId", nirvanaRequestId) +
            (withPoliciesListReload
                ? `?${GENERAL_CONFIG.urlSearchParamsKeys.reloadPoliciesList}=true`
                : "")

        navigate(neededUrl)
    }

    const onDeleteOverride = () => {
        if (!selectedPracticeRole) {
            return
        }

        ConfirmationHelper.enableConfirmation(
            () => {
                if (!overridesState.selectedPolicyOverride) {
                    return
                }

                dispatch(
                    deleteSingleCoverageOverride({
                        practice: selectedPracticeRole.practice,
                        payload: {
                            overrideNirvanaRequestId:
                                overridesState.selectedPolicyOverride
                                    .nirvanaRequestId,
                        },
                        onSuccess: (resultNirvanaRequestId: string) => {
                            if (
                                nirvanaRequestId !== resultNirvanaRequestId ||
                                !getSinglePolicy ||
                                !reloadPoliciesList
                            ) {
                                onNavigateToPolicyByRequestId(
                                    resultNirvanaRequestId,
                                )
                            } else {
                                getSinglePolicy(true, () => {
                                    reloadPoliciesList()
                                })
                            }
                        },
                    }),
                )
            },
            undefined,
            `Override will be permanently removed`,
        )
    }

    const onOverrideDone = (result: ICoverageOverrideResult) => {
        const successfulOverride = result.success[0]

        if (result.errors.length || !successfulOverride) {
            result.errors.forEach(item => {
                ToastrHelper.error(item.msg, undefined, true)
            })

            return
        }

        ToastrHelper.success(
            `Override was successfully ${
                overridesState.selectedPolicyOverride ? "updated" : "created"
            }`,
        )

        setIsOverrideModeActive(false)

        // In some cases it returns same - nirvanaRequestId
        // which shouldn't happen, but we need gracefully handle this
        // So in this case just reload everything (not redirect)
        if (
            nirvanaRequestId === successfulOverride.nirvanaRequestId &&
            getSinglePolicy &&
            reloadPoliciesList
        ) {
            getSinglePolicy(true, () => {
                reloadPoliciesList()
            })

            // Set this override in state
            dispatch(
                coverageOverridesSetSelectedPolicyOverride(successfulOverride),
            )
        } else {
            onNavigateToPolicyByRequestId(successfulOverride.nirvanaRequestId)
        }
    }

    const onSaveOverride = (data: any) => {
        if (
            !selectedPracticeRole ||
            !coverageCheckIdentificationData ||
            !nirvanaRequestId
        ) {
            return
        }

        const typedData = mapCreationFormData(data, defaultData)

        const validationResult = validateOverrideCreation(typedData)

        if (!validationResult) {
            return
        }

        dispatch(
            createCoverageOverrides({
                practice: selectedPracticeRole.practice,
                payload: {
                    data: [
                        {
                            policyIdentification: {
                                ...coverageCheckIdentificationData,
                                network:
                                    CoverageHistoryHelper.getCoverageCheckNetwork(
                                        coverageCheckHistory,
                                    ),
                            },
                            formData: typedData,
                            currentRequestId: nirvanaRequestId,
                        },
                    ],
                },
                onSuccess: onOverrideDone,
            }),
        )
    }

    // Monitor difference of default data and dataWatcher
    // If default form was changed -> activate save button
    const isFormSubmitDisabled = useMemo<boolean>(() => {
        return (
            JSON.stringify(defaultData.form) === JSON.stringify(dataWatcher) ||
            !dataWatcher.expirationDate
        )
    }, [JSON.stringify(defaultData.form), JSON.stringify(dataWatcher)])

    return {
        // vars
        isOverridesEnabled,
        isOverrideModeActive,
        setIsOverrideModeActive,
        overridesFormKey,
        setOverridesFormKey,
        canOverridePolicyCoverage,
        isDeleteOverrideLoading,
        isGetSingleOverrideLoading,
        isCreateOverrideLoading,
        isOverrideActionsDisabled,
        defaultData,
        suggestedOverrideExpirationDate,
        minOverrideExpirationDate,
        dataWatcher,
        // methods
        overridesFormMethods,
        onDeleteOverride,
        onResetOverride,
        onSaveOverride,
        onUseSuggestedOverrideExpiration,
        isFormSubmitDisabled,
    }
}
